import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ICONMeritAdminDashboard.css';

function ICONMeritAdminDashboard() {
  const [zones, setZones] = useState([]);
  const [regions, setRegions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [judges, setJudges] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [activeShows, setActiveShows] = useState([]);
  const [editingShowId, setEditingShowId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    regionId: '',
    regionIds: [],
    location: '',
    region: '',
    startDate: '',
    endDate: '',
    entryFee: 500,
    decor: '',
    theme: '',
    weather: '',
    layout: '',
    ringStyle: '',
    colorScheme: '',
    smallDogSetup: '',
    username: localStorage.getItem('username') || '',
    featuredJudge: '',
    specialtyBreed: '',
    totalPoints: 20,
    rubric: {
      components: [
        { name: 'Show Depiction', points: 10, originalKey: 'showDepiction' },
        { name: 'Dog Accuracy', points: 6, originalKey: 'dogAccuracy' },
        { name: 'Artistic Quality', points: 2, originalKey: 'artisticQuality' },
        { name: 'Creativity', points: 2, originalKey: 'creativity' },
      ],
      bonuses: [
        { name: 'Short Story', points: 0, originalKey: 'shortStory' },
        { name: 'Extra Images', points: 0, originalKey: 'extraImages' },
        { name: 'Handler', points: 0, originalKey: 'handler' },
      ],
    },
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const playerId = localStorage.getItem('playerId');
    if (!playerId) {
      setError('No player ID found. Please log in.');
      navigate('/login');
      return;
    }

    const backendUrl = `${API_BASE_URL}`;
    // Verify admin status
    fetch(`${backendUrl}/players/${playerId}`, { headers: { 'player-id': playerId } })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (data.role !== 'admin') {
          setError('Admin access required');
          navigate('/login');
        } else {
          setIsAdmin(true);
        }
      })
      .catch((err) => {
        setError(`Failed to verify admin status: ${err.message}`);
        navigate('/login');
      });

    // Fetch zones, regions, judges, breeds
    fetch(`${backendUrl}/merit-confo/zones`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => setZones(data))
      .catch((err) => setError(err.message));

    fetch(`${backendUrl}/merit-confo/regions`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => setRegions(data))
      .catch((err) => setError(`Failed to fetch regions: ${err.message}`));

    fetch(`${backendUrl}/merit-confo/npc-judges`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => setJudges(data))
      .catch((err) => setError(`Failed to fetch judges: ${err.message}`));

    fetch(`${backendUrl}/merit-confo/breeds`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => setBreeds(data))
      .catch((err) => setError(`Failed to fetch breeds: ${err.message}`));

    // Fetch active shows for editing
    fetch(`${backendUrl}/merit-confo/shows/active`, {
      headers: { 'player-id': playerId }
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => setActiveShows(data))
      .catch((err) => setError(`Failed to fetch active shows: ${err.message}`));
  }, [navigate]);

  useEffect(() => {
    if (formData.regionIds && formData.regionIds.length) {
      const filteredLocations = [];
      regions
        .filter((region) => formData.regionIds.includes(region.regionId))
        .forEach((region) => {
          region.locations.forEach((loc) => {
            filteredLocations.push({
              name: loc.name,
              region: region.name,
              regionId: region.regionId,
              display: `${loc.name}, ${region.name}, RegionId ${region.regionId}`,
            });
          });
        });
      setLocations(filteredLocations);
      setFormData((prev) => ({ ...prev, location: '', region: '' }));
    } else {
      setLocations([]);
    }
  }, [formData.regionIds, regions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRubricChange = (category, index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      rubric: {
        ...prev.rubric,
        [category]: prev.rubric[category].map((item, i) =>
          i === index ? { ...item, [field]: field === 'points' ? parseInt(value) || 0 : value } : item
        ),
      },
    }));
  };

  const addRubricItem = (category) => {
    setFormData((prev) => ({
      ...prev,
      rubric: {
        ...prev.rubric,
        [category]: [
          ...prev.rubric[category],
          { name: `New ${category.slice(0, -1)}`, points: 0, originalKey: `custom_${Date.now()}_${Math.random().toString(36).slice(2)}` },
        ],
      },
    }));
  };

  const removeRubricItem = (category, index) => {
    setFormData((prev) => ({
      ...prev,
      rubric: {
        ...prev.rubric,
        [category]: prev.rubric[category].filter((_, i) => i !== index),
      },
    }));
  };

  const resetRubric = () => {
    setFormData((prev) => ({
      ...prev,
      totalPoints: 20,
      rubric: {
        components: [
          { name: 'Show Depiction', points: 10, originalKey: 'showDepiction' },
          { name: 'Dog Accuracy', points: 6, originalKey: 'dogAccuracy' },
          { name: 'Artistic Quality', points: 2, originalKey: 'artisticQuality' },
          { name: 'Creativity', points: 2, originalKey: 'creativity' },
        ],
        bonuses: [
          { name: 'Short Story', points: 0, originalKey: 'shortStory' },
          { name: 'Extra Images', points: 0, originalKey: 'extraImages' },
          { name: 'Handler', points: 0, originalKey: 'handler' },
        ],
      },
    }));
  };

  const handleRegionIdChange = (e) => {
    const selectedZone = zones.find((z) => z.displayName === e.target.value);
    setFormData({
      ...formData,
      regionId: selectedZone ? selectedZone.regionIds[0] : '',
      regionIds: selectedZone ? selectedZone.regionIds : [],
    });
  };

  const handleLocationChange = (e) => {
    const selected = locations.find((loc) => loc.display === e.target.value);
    if (selected) {
      setFormData({
        ...formData,
        location: selected.name,
        region: selected.region,
        regionId: selected.regionId,
      });
    }
  };

  const randomizeJudge = () => {
    if (judges.length > 0) {
      const randomJudge = judges[Math.floor(Math.random() * judges.length)].name;
      setFormData({ ...formData, featuredJudge: randomJudge });
    } else {
      alert('No judges available to randomize.');
    }
  };

  const randomizeBreed = () => {
    if (breeds.length > 0) {
      const randomBreed = breeds[Math.floor(Math.random() * breeds.length)].breedName;
      setFormData({ ...formData, specialtyBreed: randomBreed });
    } else {
      alert('No breeds available to randomize.');
    }
  };

  const venueOptions = {
    decor: [
      'draped elegantly with floral garlands',
      'festooned with velvet banners',
      'adorned with rustic wooden accents',
      'sparkling with crystal chandeliers',
      'vibrant with tropical palm fronds',
    ],
    theme: [
      'Majestic Desert Mirage',
      'Enchanted Winter Palace',
      'Serenity by the Coast',
      'Rustic Mountain Splendor',
      'Sleek Urban Elegance',
    ],
    weather: [
      'bathed in golden sunlight with a gentle breeze',
      'crisp and clear under a starry sky',
      'softly lit with scattered clouds',
      'well-lit with a cool ambiance',
      'warmly illuminated with a cozy glow',
    ],
    layout: [
      'a grand circular outdoor arena',
      'an intimate indoor stadium with tiered seating',
      'an open meadow with a central stage',
      'a charming pavilion with side galleries',
      'a dramatic amphitheater under the open sky',
    ],
    ringStyle: [
      'traditional white X-shaped ring gates',
      'elegant rope and stanchions',
      'sturdy metal wire fencing',
      'classic wooden picket barriers',
      'ornate wrought iron dividers',
    ],
    colorScheme: [
      'emerald green and radiant gold',
      'navy blue and shimmering silver',
      'crimson red and ivory white',
      'lavender purple and soft pink',
      'earthy brown and vibrant orange',
    ],
    smallDogSetup: [
      'on a grooming table covered with a themed tablecloth',
      'on an uncovered professional grooming table',
      'on a raised platform with a custom velvet cover',
      'on a table adorned with floral accents',
      'on a sleek modern table with a non-slip surface',
    ],
  };

  const generateVenueDetails = () => {
    const isIndoor = ['an intimate indoor stadium with tiered seating', 'a charming pavilion with side galleries'].includes(
      formData.layout
    );
    const weatherOptionsFiltered = isIndoor
      ? venueOptions.weather.filter((w) => ['well-lit with a cool ambiance', 'warmly illuminated with a cozy glow'].includes(w))
      : venueOptions.weather.filter((w) => !['well-lit with a cool ambiance', 'warmly illuminated with a cozy glow'].includes(w));

    setFormData({
      ...formData,
      decor: venueOptions.decor[Math.floor(Math.random() * venueOptions.decor.length)],
      theme: venueOptions.theme[Math.floor(Math.random() * venueOptions.theme.length)],
      weather: weatherOptionsFiltered[Math.floor(Math.random() * weatherOptionsFiltered.length)],
      layout: venueOptions.layout[Math.floor(Math.random() * venueOptions.layout.length)],
      ringStyle: venueOptions.ringStyle[Math.floor(Math.random() * venueOptions.ringStyle.length)],
      colorScheme: venueOptions.colorScheme[Math.floor(Math.random() * venueOptions.colorScheme.length)],
      smallDogSetup: venueOptions.smallDogSetup[Math.floor(Math.random() * venueOptions.smallDogSetup.length)],
    });
  };

  const handleEditShow = (show) => {
    setEditingShowId(show._id);
    setFormData({
      name: show.name,
      regionId: show.regionId || '',
      regionIds: show.regionId ? [show.regionId] : [],
      location: show.location,
      region: show.region,
      startDate: new Date(show.startDate).toISOString().split('T')[0],
      endDate: new Date(show.endDate).toISOString().split('T')[0],
      entryFee: show.entryFee,
      decor: show.venueElements.decor,
      theme: show.venueElements.theme,
      weather: show.venueElements.weather,
      layout: show.venueElements.layout,
      ringStyle: show.venueElements.ringStyle,
      colorScheme: show.venueElements.colorScheme,
      smallDogSetup: show.venueElements.smallDogSetup,
      username: show.username,
      featuredJudge: show.featuredJudge,
      specialtyBreed: show.specialtyBreed,
      totalPoints: show.totalPoints,
      rubric: {
        components: show.rubric.components,
        bonuses: show.rubric.bonuses,
      },
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.location ||
      !formData.region ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.entryFee ||
      !formData.decor ||
      !formData.theme ||
      !formData.weather ||
      !formData.layout ||
      !formData.ringStyle ||
      !formData.colorScheme ||
      !formData.smallDogSetup ||
      !formData.featuredJudge ||
      !formData.specialtyBreed ||
      !formData.rubric.components.length
    ) {
      alert('Please fill out all required fields and include at least one rubric component.');
      return;
    }

    const totalComponentPoints = formData.rubric.components.reduce((sum, c) => sum + c.points, 0);
    if (totalComponentPoints > formData.totalPoints) {
      alert(`Total component points (${totalComponentPoints}) cannot exceed total show points (${formData.totalPoints}).`);
      return;
    }

    const venueElements = {
      decor: formData.decor,
      theme: formData.theme,
      weather: formData.weather,
      layout: formData.layout,
      ringStyle: formData.ringStyle,
      colorScheme: formData.colorScheme,
      smallDogSetup: formData.smallDogSetup,
    };

    const showData = {
      name: formData.name,
      region: formData.region,
      location: formData.location,
      startDate: formData.startDate,
      endDate: formData.endDate,
      entryFee: formData.entryFee,
      venueElements,
      username: formData.username,
      featuredJudge: formData.featuredJudge,
      specialtyBreed: formData.specialtyBreed,
      totalPoints: formData.totalPoints,
      rubric: {
        components: formData.rubric.components,
        bonuses: formData.rubric.bonuses,
      },
    };

    const playerId = localStorage.getItem('playerId');
    const url = editingShowId ? `${API_BASE_URL}/merit-confo/shows/${editingShowId}` : `${API_BASE_URL}/merit-confo/shows`;
    const method = editingShowId ? 'PUT' : 'POST';

    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'player-id': playerId },
      body: JSON.stringify(showData),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((err) => {
            throw new Error(`HTTP ${res.status}: ${err.error || res.statusText}`);
          });
        }
        return res.json();
      })
      .then((data) => {
        alert(editingShowId ? 'Show updated successfully!' : 'Merit-Confo Show created successfully!');
        setFormData({
          name: '',
          regionId: '',
          regionIds: [],
          location: '',
          region: '',
          startDate: '',
          endDate: '',
          entryFee: 500,
          decor: '',
          theme: '',
          weather: '',
          layout: '',
          ringStyle: '',
          colorScheme: '',
          smallDogSetup: '',
          username: localStorage.getItem('username') || '',
          featuredJudge: '',
          specialtyBreed: '',
          totalPoints: 20,
          rubric: {
            components: [
              { name: 'Show Depiction', points: 10, originalKey: 'showDepiction' },
              { name: 'Dog Accuracy', points: 6, originalKey: 'dogAccuracy' },
              { name: 'Artistic Quality', points: 2, originalKey: 'artisticQuality' },
              { name: 'Creativity', points: 2, originalKey: 'creativity' },
            ],
            bonuses: [
              { name: 'Short Story', points: 0, originalKey: 'shortStory' },
              { name: 'Extra Images', points: 0, originalKey: 'extraImages' },
              { name: 'Handler', points: 0, originalKey: 'handler' },
            ],
          },
        });
        setEditingShowId(null);
        setLocations([]);
        // Refresh active shows
        fetch(`${API_BASE_URL}/merit-confo/shows/active`, {
          headers: { 'player-id': playerId }
        })
          .then((res) => res.json())
          .then((data) => setActiveShows(data))
          .catch((err) => setError(`Failed to refresh active shows: ${err.message}`));
      })
      .catch((err) => {
        alert(`Error ${editingShowId ? 'updating' : 'creating'} show: ${err.message}`);
      });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Detroit'
    });
  };

  const getFlavorText = () => {
    const locationParts = formData.location.split(' ');
    const city = locationParts.slice(0, -1).join(' ');
    const stateOrCountry = locationParts.slice(-1)[0];
    const isIndoor = ['an intimate indoor stadium with tiered seating', 'a charming pavilion with side galleries'].includes(
      formData.layout
    );
    const venuePhrase = isIndoor
      ? `Entrants compete in ${formData.layout.toLowerCase()}, illuminated by ${formData.weather.toLowerCase()}`
      : `Entrants compete in ${formData.layout.toLowerCase()} under ${formData.weather.toLowerCase()}`;

    const rubricDescription = formData.rubric.components
      .map((c) => `${c.name} (${c.points} pts)`);
    const bonusDescriptions = formData.rubric.bonuses
      .filter((b) => b.points > 0)
      .map((b) => `${b.name} (up to ${b.points} pts)`);
    const judgingText = `Judged on: ${rubricDescription.join(', ')}${
      bonusDescriptions.length ? `. Bonuses: ${bonusDescriptions.join(', ')}.` : '.'
    }`;

    return `
      Step into the spotlight at the prestigious <strong>${formData.name}</strong>, held in the heart of ${city}, ${stateOrCountry} (${
      formData.region
    }) from ${formatDate(formData.startDate)} at 8:00 AM to ${formatDate(formData.endDate)} at 5:00 PM EDT! The theme of this event is ${
      formData.theme.toLowerCase()
    }. The venue is ${formData.decor.toLowerCase()} in a stunning ${formData.colorScheme.toLowerCase()} color palette. ${venuePhrase}, with rings defined by ${
      formData.ringStyle.toLowerCase()
    }. Small dogs are examined ${formData.smallDogSetup.toLowerCase()}. This ${formData.specialtyBreed} Specialty Show will be judged by the esteemed ${
      formData.featuredJudge
    }. ${judgingText} Total points: ${formData.totalPoints}. Entry fee: ${formData.entryFee} DFG$.
    `;
  };

  const handlePreview = () => {
    if (
      !formData.name ||
      !formData.location ||
      !formData.region ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.entryFee ||
      !formData.decor ||
      !formData.theme ||
      !formData.weather ||
      !formData.layout ||
      !formData.ringStyle ||
      !formData.colorScheme ||
      !formData.smallDogSetup ||
      !formData.featuredJudge ||
      !formData.specialtyBreed ||
      !formData.rubric.components.length
    ) {
      alert('Please fill out all fields and include at least one rubric component before previewing.');
      return;
    }
    setShowPreview(true);
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  const cancelEdit = () => {
    setEditingShowId(null);
    setFormData({
      name: '',
      regionId: '',
      regionIds: [],
      location: '',
      region: '',
      startDate: '',
      endDate: '',
      entryFee: 500,
      decor: '',
      theme: '',
      weather: '',
      layout: '',
      ringStyle: '',
      colorScheme: '',
      smallDogSetup: '',
      username: localStorage.getItem('username') || '',
      featuredJudge: '',
      specialtyBreed: '',
      totalPoints: 20,
      rubric: {
        components: [
          { name: 'Show Depiction', points: 10, originalKey: 'showDepiction' },
          { name: 'Dog Accuracy', points: 6, originalKey: 'dogAccuracy' },
          { name: 'Artistic Quality', points: 2, originalKey: 'artisticQuality' },
          { name: 'Creativity', points: 2, originalKey: 'creativity' },
        ],
        bonuses: [
          { name: 'Short Story', points: 0, originalKey: 'shortStory' },
          { name: 'Extra Images', points: 0, originalKey: 'extraImages' },
          { name: 'Handler', points: 0, originalKey: 'handler' },
        ],
      },
    });
    setLocations([]);
  };

  if (error) {
    return <p style={{ textAlign: 'center', color: 'red' }}>{error}</p>;
  }

  if (!isAdmin) {
    return <p style={{ textAlign: 'center', color: '#333' }}>Checking admin status...</p>;
  }

  return (
    <div className="merit-confo-admin-container">
      <h2>{editingShowId ? 'Update iCON Merit-Confo Show' : 'Create iCON Merit-Confo Show'}</h2>
      
      {activeShows.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Active Shows to Edit</h3>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {activeShows.map((show) => (
              <li key={show._id} style={{ margin: '10px 0' }}>
                <span>{show.name} ({formatDate(show.startDate)} - {formatDate(show.endDate)})</span>
                <button
                  onClick={() => handleEditShow(show)}
                  style={{
                    marginLeft: '10px',
                    padding: '5px 10px',
                    backgroundColor: '#1E88E5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <label>
          <span>Show Name:</span>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Mapledale iCON Merit, June 2025"
            required
          />
        </label>
        <label>
          <span>Zone:</span>
          <select
            name="regionId"
            value={zones.find((z) => z.regionIds.includes(parseInt(formData.regionId)))?.displayName || ''}
            onChange={handleRegionIdChange}
            required
          >
            <option value="">Select Zone</option>
            {zones.map((zone) => (
              <option key={zone.displayName} value={zone.displayName}>
                {zone.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Location:</span>
          <select
            name="location"
            value={formData.location ? `${formData.location}, ${formData.region}, RegionId ${formData.regionId}` : ''}
            onChange={handleLocationChange}
            required
            disabled={!formData.regionIds.length}
          >
            <option value="">Select Location</option>
            {locations.map((loc) => (
              <option key={`${loc.name}-${loc.region}`} value={loc.display}>
                {loc.display}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Start Date (8:00 AM EDT):</span>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          <span>End Date (5:00 PM EDT):</span>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            required
          />
        </label>
        <label>
          <span>Entry Fee (DFG$, minimum 500):</span>
          <input
            type="number"
            name="entryFee"
            value={formData.entryFee}
            onChange={(e) => setFormData({ ...formData, entryFee: parseInt(e.target.value) || 500 })}
            min="500"
            required
          />
        </label>
        <label>
          <span>Venue Details:</span>
          <button
            type="button"
            onClick={generateVenueDetails}
            style={{
              padding: '8px 15px',
              backgroundColor: '#1E88E5',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              marginBottom: '10px',
            }}
          >
            Generate Venue Details
          </button>
        </label>
        {['decor', 'theme', 'weather', 'layout', 'ringStyle', 'colorScheme', 'smallDogSetup'].map((field) => (
          <label key={field}>
            <span>{field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}:</span>
            <div style={{ display: 'block', marginBottom: '10px' }}>
              <select
                name={`${field}Select`}
                value=""
                onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                style={{ width: '100%', marginBottom: '5px' }}
              >
                <option value="">Select {field.charAt(0).toUpperCase() + field.slice(1)}</option>
                {venueOptions[field].map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name={field}
                value={formData[field]}
                onChange={handleChange}
                placeholder={`e.g., ${venueOptions[field][0]}`}
                required
                style={{ width: '100%' }}
              />
            </div>
          </label>
        ))}
        <label>
          <span>Admin Username:</span>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            disabled
          />
        </label>
        <label>
          <span>Featured Judge:</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select
              name="featuredJudge"
              value={formData.featuredJudge}
              onChange={handleChange}
              required
            >
              <option value="">Select Judge</option>
              {judges.length > 0 ? (
                judges.map((judge) => (
                  <option key={judge._id} value={judge.name}>
                    {judge.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No judges available
                </option>
              )}
            </select>
            <button
              type="button"
              onClick={randomizeJudge}
              style={{
                padding: '8px 15px',
                backgroundColor: '#1E88E5',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Randomize
            </button>
          </div>
        </label>
        <label>
          <span>Specialty Breed:</span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <select
              name="specialtyBreed"
              value={formData.specialtyBreed}
              onChange={handleChange}
              required
            >
              <option value="">Select Specialty Breed</option>
              {breeds.length > 0 ? (
                breeds.map((breed) => (
                  <option key={breed._id} value={breed.breedName}>
                    {breed.breedName}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No breeds available
                </option>
              )}
            </select>
            <button
              type="button"
              onClick={randomizeBreed}
              style={{
                padding: '8px 15px',
                backgroundColor: '#1E88E5',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Randomize
            </button>
          </div>
        </label>
        <label>
          <span>Judging Rubric:</span>
          <div style={{ marginBottom: '10px' }}>
            <label>
              <span>Total Show Points:</span>
              <input
                type="number"
                name="totalPoints"
                value={formData.totalPoints}
                onChange={handleChange}
                min="1"
                required
                style={{ width: '100px' }}
              />
            </label>
            <div>
              <button
                type="button"
                onClick={resetRubric}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  margin: '10px 0',
                }}
              >
                Reset to Recommended Rubric
              </button>
            </div>
            <h4>Components</h4>
            {formData.rubric.components.map((component, index) => (
              <div key={component.originalKey} style={{ marginLeft: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={component.name}
                  onChange={(e) => handleRubricChange('components', index, 'name', e.target.value)}
                  placeholder="Component Name"
                  style={{ width: '200px', marginRight: '10px' }}
                />
                <input
                  type="number"
                  value={component.points}
                  onChange={(e) => handleRubricChange('components', index, 'points', parseInt(e.target.value) || 0)}
                  min="0"
                  style={{ width: '80px', marginRight: '10px' }}
                />
                <button
                  type="button"
                  onClick={() => removeRubricItem('components', index)}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#B91C1C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  X
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addRubricItem('components')}
              style={{
                padding: '8px 15px',
                backgroundColor: '#1E88E5',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                margin: '10px 0',
              }}
            >
              Add Component
            </button>
            <h4>Bonuses</h4>
            {formData.rubric.bonuses.map((bonus, index) => (
              <div key={bonus.originalKey} style={{ marginLeft: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={bonus.name}
                  onChange={(e) => handleRubricChange('bonuses', index, 'name', e.target.value)}
                  placeholder="Bonus Name"
                  style={{ width: '200px', marginRight: '10px' }}
                />
                <input
                  type="number"
                  value={bonus.points}
                  onChange={(e) => handleRubricChange('bonuses', index, 'points', parseInt(e.target.value) || 0)}
                  min="0"
                  style={{ width: '80px', marginRight: '10px' }}
                />
                <button
                  type="button"
                  onClick={() => removeRubricItem('bonuses', index)}
                  style={{
                    padding: '5px 10px',
                    backgroundColor: '#B91C1C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  X
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addRubricItem('bonuses')}
              style={{
                padding: '8px 15px',
                backgroundColor: '#1E88E5',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                margin: '10px 0',
              }}
            >
              Add Bonus
            </button>
          </div>
        </label>
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={handlePreview}
            style={{
              padding: '8px 15px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            Preview Show Description
          </button>
          <button
            type="submit"
            style={{
              padding: '8px 15px',
              backgroundColor: '#1E88E5',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
            }}
          >
            {editingShowId ? 'Update Show' : 'Create Show'}
          </button>
          {editingShowId && (
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                padding: '8px 15px',
                backgroundColor: '#B91C1C',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      {showPreview && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              maxWidth: '600px',
              width: '90%',
              position: 'relative',
            }}
          >
            <h3>Show Preview</h3>
            <div dangerouslySetInnerHTML={{ __html: getFlavorText() }} />
            <button
              onClick={closePreview}
              style={{
                padding: '8px 15px',
                backgroundColor: '#B91C1C',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                position: 'absolute',
                top: '10px',
                right: '10px',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ICONMeritAdminDashboard;